import React, {
  useCallback, useState, useEffect, useMemo,
} from 'react';
import * as Styles from './ModalPanels.styles';
import { shallowEqual, useSelector, useDispatch } from 'react-redux';
import useLocale from '@/hooks/useLocale';
import { useParams } from 'react-router-dom';
import { SelectField } from 'gatorcomponents';

import { formatCurrency } from '@/utils/Formatters/formatCurrency';
import { formatAddonsList } from '@/utils/Formatters/formatAddonsList';

import { vpsOciActions } from '@/redux/modules/productDetail/vpsOci';
import { billingAnalytics } from '@/analytics';

import { getIconBySlug } from '@/pages/Products/VpsOciDetailPage/utils';
import VpsStoreModal from '@/pages/Products/VpsOciDetailPage/pages/ProductTemplatesPage/commons/VpsStoreModal';

const ModalPanels = ({
  onClose,
  selectedItem,
  modalTypeCustom,
  retentionPageInfo,
}) => {
  const dispatch = useDispatch();

  const params = useParams();
  const { id, subscriptionId } = params;
  const hostingId = subscriptionId || id;

  const [modalType, setModalType] = useState('selectionPanels');
  const [previousModalType, setPreviousModalType] = useState(null);
  const [bodyData, setBodyData] = useState(null);
  const [selectedAccountId, setSelectedAccountId] = useState(null);
  const [selectedBillingCycle, setSelectedBillingCycle] = useState(null);
  const [requestChangeImage, setRequestChangeImage] = useState(false);

  const {
    availableOptions,
    addons,
    productName,
    changeImageLoading,
  } = useSelector(state => ({
    availableOptions: state.vpsOci?.data?.availableOptions,
    addons: state.vpsOci?.data?.addons,
    productName: state.vpsOci?.data?.productName,
    changeImageLoading: state.vpsOci?.changeImage?.loading,
  }), shallowEqual);
  const { panels } = availableOptions || {};

  const { productDetails: productDetailsLocale, billing: billingLocale } = useLocale();
  const { vpsOci: localeVpsOci } = productDetailsLocale;

  const { panels: localeVpsOciStoreDetailsPanels } = localeVpsOci.store && localeVpsOci.store.details;
  const { alert: localeVpsOciStoreModalAlert } = localeVpsOci && localeVpsOci.store && localeVpsOci.store.modal;
  const { panels: localeVpsOciStoreChangeApplicationsModalPanels } = localeVpsOci.store && localeVpsOci.store.changeApplications && localeVpsOci.store.changeApplications.modal;

  const { subscriptionsV2: subscriptionAnalyticsV2, hostingAndServers: hostingAndServersAnalytics } = billingAnalytics;

  const {
    paymentCycle: paymentCycleLocale,
  } = billingLocale;

  const addonsPaidList = useMemo(() => (addons && addons.filter(addon => addon.isPaid)) || [], [addons]);
  const listAddonsNames = useMemo(() => (
    addonsPaidList && addonsPaidList.map(addon => addon.name)
  ), [addonsPaidList]);
  const formattedAddonsText = useMemo(() => formatAddonsList(listAddonsNames), [listAddonsNames]);
  const hasPaidAddons = useMemo(() => (addonsPaidList && addonsPaidList.length > 0), [addonsPaidList]);

  const selectedPanels = useMemo(() => panels && panels.find(panel => selectedItem && panel.slug === selectedItem),
    [panels, selectedItem]);

  const panelAddonsList = selectedPanels && selectedPanels.addons;
  const hasAddonsPaidInPanelSelected = panelAddonsList && panelAddonsList.length > 0 && panelAddonsList.some(addon => (addon.seats > 0 && (addon.billingcycles && addon.billingcycles.length > 0)));

  const provisionedSo = selectedPanels && selectedPanels.operationalSystems && selectedPanels.operationalSystems.name;

  const {
    name: panelName = '',
    slug: panelslug = null,
  } = selectedPanels || {};

  const disableSelectionPanelsConfirmButton = (!selectedAccountId || !selectedBillingCycle) && hasAddonsPaidInPanelSelected;

  const { isRetentionCancellationPage, retentionGetGALabel } = retentionPageInfo || {};

  useEffect(() => {
    if (modalTypeCustom) {
      setModalType(modalTypeCustom);
    }
  }, [modalTypeCustom]);

  const getDescriptionBySlug = useCallback((slug) => {
    const item = localeVpsOciStoreDetailsPanels[slug];
    return item ? item.description : null;
  }, [localeVpsOciStoreDetailsPanels]);

  const handleCloseModal = useCallback(() => {
    onClose && onClose();
    setBodyData(null);
    setSelectedAccountId(null);
    setSelectedBillingCycle(null);

    if (isRetentionCancellationPage) {
      subscriptionAnalyticsV2.retentionPainelModalCancelClickV2(selectedItem, retentionGetGALabel);
    } else if (!requestChangeImage) {
      hostingAndServersAnalytics.hostingOsSelectVersionCancelClick(selectedItem, productName);
    }
  }, [onClose, subscriptionAnalyticsV2, selectedItem, retentionGetGALabel, isRetentionCancellationPage, hostingAndServersAnalytics, productName, requestChangeImage]);

  const handleChangeAccountSeats = useCallback((selectedOption) => {
    setSelectedAccountId(selectedOption.value);
    setSelectedBillingCycle(null);
  }, []);

  const handleChangeAccountFrequency = useCallback((selectedOption) => {
    setSelectedBillingCycle(selectedOption.value);
  }, []);

  const handleBackStep = useCallback(() => {
    setModalType(previousModalType);
    setBodyData(null);

    if (isRetentionCancellationPage) {
      subscriptionAnalyticsV2.retentionPainelModalCancelClickV2(selectedItem, retentionGetGALabel);
      return;
    }

    if (hasAddonsPaidInPanelSelected) {
      hostingAndServersAnalytics.hostingOsPaymentBackClick(selectedItem, productName);
    } else {
      hostingAndServersAnalytics.hostingOsInstallBackClick(selectedItem, productName);
    }
  }, [previousModalType, subscriptionAnalyticsV2, selectedItem, retentionGetGALabel, isRetentionCancellationPage, hostingAndServersAnalytics, productName, hasAddonsPaidInPanelSelected]);

  const handleStepSelectAccount = useCallback(() => {
    const addonId = (selectedPanels && selectedPanels.addons && selectedPanels.addons.length > 0) && selectedPanels.addons[0].id;
    const newImageId = selectedAccountId || addonId;

    setBodyData({
      hostingId,
      newImageId,
      imageType: 'panel',
      billingCycle: selectedBillingCycle,
    });

    setPreviousModalType(modalType);

    if (isRetentionCancellationPage) {
      subscriptionAnalyticsV2.retentionPainelModalContinueClickV2(selectedItem, retentionGetGALabel);
    } else {
      hostingAndServersAnalytics.hostingOsSelectVersionContinueClick(selectedItem, productName);
    }

    if (hasPaidAddons) {
      setModalType('confirmWithPanelPaidActive');
      return;
    }
    setModalType('confirmWithoutPanelPaidActive');
  }, [selectedAccountId, selectedBillingCycle, hostingId, selectedPanels, subscriptionAnalyticsV2, selectedItem, retentionGetGALabel, isRetentionCancellationPage, hostingAndServersAnalytics, productName, hasPaidAddons, modalType]);

  const handleStepConfirm = useCallback(() => {
    if (hostingId) {
      setRequestChangeImage(true);
      dispatch(vpsOciActions.changeImage.request(bodyData));

      if (isRetentionCancellationPage) {
        subscriptionAnalyticsV2.retentionPainelModalConfirmClickV2(selectedItem, retentionGetGALabel);
        return;
      }

      if (hasAddonsPaidInPanelSelected) {
        hostingAndServersAnalytics.hostingOsPaymentConfirmClick(selectedItem, productName);
      } else {
        hostingAndServersAnalytics.hostingOsInstallConfirmClick(selectedItem, productName);
      }
    }
  }, [hostingId, dispatch, bodyData, subscriptionAnalyticsV2, selectedItem, retentionGetGALabel, isRetentionCancellationPage, hostingAndServersAnalytics, productName, hasAddonsPaidInPanelSelected]);

  useEffect(() => {
    if (requestChangeImage && !changeImageLoading) {
      handleCloseModal();
      setRequestChangeImage(false);
    }
  }, [changeImageLoading, handleCloseModal, requestChangeImage]);

  const modalPanelsDetails = useMemo(() => {
    const icon = panelslug ? getIconBySlug(panelslug) : null;
    const description = panelslug ? getDescriptionBySlug(panelslug) : null;

    const selectedSOInfoData = {
      name: panelName,
      description: description || '',
      icon: icon ? (
        <Styles.SelectedPanelTitleIcon
          src={icon}
          alt={panelName}
          data-testid={`vpsoci-templates-panel-icon-${panelslug}`}
        />
      ) : null,
    };

    if (!selectedSOInfoData) return null;

    const getAccountsLabel = seats => (seats === 9999
      ? localeVpsOciStoreChangeApplicationsModalPanels.qtyAccountsTextUnlimited
      : localeVpsOciStoreChangeApplicationsModalPanels.qtyAccountsText(seats));

    const optionsAccountsList = hasAddonsPaidInPanelSelected
      ? panelAddonsList.map(({ seats, name, id }) => ({
        label: `${name} - ${getAccountsLabel(seats)}`,
        value: id,
      }))
      : null;

    const selectedAddonBillingcycles = hasAddonsPaidInPanelSelected
      ? panelAddonsList.find(addon => addon.id === selectedAccountId)
      : null;

    const optionsPaymentFrequencyList = (selectedAddonBillingcycles && selectedAddonBillingcycles.billingcycles)
      ? selectedAddonBillingcycles.billingcycles.map(frequency => ({
        label: `${paymentCycleLocale[frequency.cycle]} (${frequency.currencyPrefix} ${formatCurrency(frequency.price)})`,
        value: frequency.cycle,
      }))
      : null;

    if (!selectedSOInfoData) return null;

    return (
      <Styles.SelectedPanel data-testid="vpsoci-templates-panel">
        <Styles.SelectedPanelTitle data-testid="vpsoci-templates-panel-name">
          {selectedSOInfoData.icon}
          {selectedSOInfoData.name}
        </Styles.SelectedPanelTitle>
        <Styles.SelectedPanelDescription data-testid="vpsoci-templates-panel-description">
          {selectedSOInfoData.description}

          {localeVpsOciStoreChangeApplicationsModalPanels.provisioned && (
            <Styles.ProvisionedText>
              {localeVpsOciStoreChangeApplicationsModalPanels.provisioned(provisionedSo)}
            </Styles.ProvisionedText>
          )}
        </Styles.SelectedPanelDescription>

        {optionsAccountsList && (
          <Styles.SelectWrapper>
            <SelectField
              placeholder={localeVpsOciStoreChangeApplicationsModalPanels.placeholder.optionsAccounts}
              options={optionsAccountsList}
              onClickSelect={option => handleChangeAccountSeats(option)}
              withScroll
              filterable
            />
          </Styles.SelectWrapper>
        )}

        {optionsPaymentFrequencyList && (
          <Styles.SelectWrapper>
            <SelectField
              key={`payment-frequency-${selectedAccountId}`}
              placeholder={localeVpsOciStoreChangeApplicationsModalPanels.placeholder.paymentFrequency}
              options={optionsPaymentFrequencyList}
              clearValue={selectedBillingCycle === null}
              onClickSelect={option => handleChangeAccountFrequency(option)}
              withScroll
              filterable
            />
          </Styles.SelectWrapper>
        )}

      </Styles.SelectedPanel>
    );
  }, [
    panelslug,
    getDescriptionBySlug,
    panelName,
    hasAddonsPaidInPanelSelected,
    panelAddonsList,
    localeVpsOciStoreChangeApplicationsModalPanels,
    paymentCycleLocale,
    provisionedSo,
    selectedAccountId,
    selectedBillingCycle,
    handleChangeAccountSeats,
    handleChangeAccountFrequency,
  ]);

  const modalConfig = useMemo(() => ({
    selectionPanels: {
      title: localeVpsOciStoreChangeApplicationsModalPanels.title,
      message: modalPanelsDetails,
      alert: null,
      testId: 'vpsoci-panels-selectionPanels-modal',
      confirmButton: {
        text: localeVpsOciStoreChangeApplicationsModalPanels.selectionPanels.confirmButton,
        action: handleStepSelectAccount,
        disabled: disableSelectionPanelsConfirmButton,
        testId: 'vpsoci-panels-selectionPanels-modal-confirm-button',
      },
      closeButton: {
        text: localeVpsOciStoreChangeApplicationsModalPanels.selectionPanels.closeButton,
        action: handleCloseModal,
        testId: 'vpsoci-panels-selectionPanels-modal-close-button',
      },
    },

    confirmWithoutPanelPaidActive: {
      title: localeVpsOciStoreChangeApplicationsModalPanels.title,
      message: localeVpsOciStoreChangeApplicationsModalPanels.confirmWithoutPanelPaidActive.message(panelName),
      alert: localeVpsOciStoreModalAlert.backup,
      agreedTerms: localeVpsOciStoreChangeApplicationsModalPanels.confirmWithoutPanelPaidActive.agreedTerms(panelName),
      testId: 'vpsoci-panels-confirmWithoutPanelPaidActive-modal',
      confirmButton: {
        text: hasAddonsPaidInPanelSelected ? localeVpsOciStoreChangeApplicationsModalPanels.confirmWithoutPanelPaidActive.confirmButtonPayment : localeVpsOciStoreChangeApplicationsModalPanels.confirmWithoutPanelPaidActive.confirmButtonInstall,
        action: handleStepConfirm,
        loading: changeImageLoading,
        testId: 'vpsoci-panels-confirmWithoutPanelPaidActive-modal-confirm-button',
      },
      closeButton: {
        text: localeVpsOciStoreChangeApplicationsModalPanels.confirmWithoutPanelPaidActive.closeButton,
        action: handleBackStep,
        testId: 'vpsoci-panels-confirmWithoutPanelPaidActive-modal-close-button',
      },
    },
    confirmWithPanelPaidActive: {
      title: localeVpsOciStoreChangeApplicationsModalPanels.title,
      message: localeVpsOciStoreChangeApplicationsModalPanels.confirmWithPanelPaidActive.message(panelName, formattedAddonsText),
      alert: localeVpsOciStoreModalAlert.backup,
      agreedTerms: localeVpsOciStoreChangeApplicationsModalPanels.confirmWithPanelPaidActive.agreedTerms(panelName, formattedAddonsText),
      testId: 'vpsoci-panels-confirmWithPanelPaidActive-modal',
      confirmButton: {
        text: hasAddonsPaidInPanelSelected ? localeVpsOciStoreChangeApplicationsModalPanels.confirmWithPanelPaidActive.confirmButtonPayment : localeVpsOciStoreChangeApplicationsModalPanels.confirmWithPanelPaidActive.confirmButtonInstall,
        action: handleStepConfirm,
        loading: changeImageLoading,
        testId: 'vpsoci-panels-confirmWithPanelPaidActive-modal-confirm-button',
      },
      closeButton: {
        text: localeVpsOciStoreChangeApplicationsModalPanels.confirmWithPanelPaidActive.closeButton,
        action: handleBackStep,
        testId: 'vpsoci-panels-confirmWithPanelPaidActive-modal-close-button',
      },
    },
  }), [
    localeVpsOciStoreChangeApplicationsModalPanels,
    modalPanelsDetails,
    handleStepSelectAccount,
    handleCloseModal,
    localeVpsOciStoreModalAlert,
    handleStepConfirm,
    handleBackStep,
    formattedAddonsText,
    panelName,
    disableSelectionPanelsConfirmButton,
    hasAddonsPaidInPanelSelected,
    changeImageLoading,
  ]);

  if (!selectedItem || !panels || Object.keys(panels).length === 0) return null;

  return (
    <VpsStoreModal
      modalType={modalType}
      modalConfig={modalConfig}
      onClose={handleCloseModal}
    />
  );
};

export default ModalPanels;
